/**
 * The widget REST routes, as functions.
 *
 * Thin on purpose: no caching, no retry policy, no state. Whether a failed send
 * is retried is a decision the composer makes with the visitor watching, and it
 * cannot be made correctly by a layer that does not know whether the message is
 * still on screen.
 *
 * Every path comes from the route object in `@axon/contracts/routes/widget`, so
 * there is no URL literal in this app to drift from the server's.
 */
import { apiRequest, setVisitorToken, type RequestOptions } from './client';
import {
  WIDGET_ROUTE,
  type SeqString,
  type WidgetConversation,
  type WidgetConversationList,
  type WidgetConversationThread,
  type WidgetSendMessageRequest,
  type WidgetSendMessageResult,
  type WidgetSession,
  type WidgetSessionEnded,
  type WidgetSessionRequest,
  type WidgetThread,
  type WidgetVisitorContextRequest,
  type WidgetVisitorContextResult,
} from './contract';

type Cancellable = Pick<RequestOptions, 'signal'>;

/**
 * A path with its query string, or the bare path when there is nothing to ask.
 *
 * The empty-string check is not tidiness: `?` with nothing after it is a
 * different URL from the one without it, it shows up that way in every access
 * log and CDN cache key, and the default page of a list is the request this app
 * makes most often.
 */
function withQuery(path: string, params: URLSearchParams): string {
  const search = params.toString();
  return search === '' ? path : `${path}?${search}`;
}

/**
 * Bootstrap a widget session.
 *
 * Called once on a cold start, and once more when the visitor submits the
 * pre-chat form — the same route, with `prechat` filled in. There is no separate
 * "submit the form" endpoint, and no `visitorToken` on the request: a bootstrap
 * always mints a FRESH credential rather than refreshing one, which is why this
 * call is `anonymous` and why the app caches the result for the life of the tab
 * (see `session-store.ts`).
 *
 * NOTE WHAT IS NOT IN THE RESPONSE: a `conversationIds[]` list. P2-28 removed it
 * deliberately. A list baked into a token is stale by construction — a
 * conversation created after the token was minted is not on it — so ownership is
 * decided by the RESTRICTIVE policy on every statement instead. There is nothing
 * here for this client to carry, which is the point.
 */
export async function openSession(
  request: WidgetSessionRequest,
  options: Cancellable & {
    /**
     * A sign-in (ADR-0249 §4) that REPLACES the session this tab holds: the
     * current token goes along as `Authorization`, so the server can revoke
     * the anonymous session once the player's exists. A stale token is refused
     * by the edge with a 401 before the JWT is even read — the caller retries
     * once without it.
     */
    readonly replacing?: boolean;
  } = {},
): Promise<WidgetSession> {
  const { replacing, ...rest } = options;
  const session = await apiRequest<WidgetSession>(WIDGET_ROUTE.session.path, {
    method: 'POST',
    body: request,
    // The bootstrap IS the authentication step, so it must not send a stale
    // Authorization header: a rejected token there would fail the whole boot
    // instead of producing a fresh session. A sign-in that replaces a live
    // session is the one exception, and says so.
    anonymous: replacing !== true,
    ...rest,
  });

  setVisitorToken(session.visitorToken);
  return session;
}

export interface ThreadQuery {
  readonly limit?: number;
  /** Scrollback: the messages immediately BEFORE this seq. A decimal int8 string. */
  readonly beforeSeq?: SeqString;
}

/**
 * The visitor's own conversation and its customer-visible messages.
 *
 * No conversation id parameter, and that is the P2-28 design showing at the
 * surface: the server resolves "which conversation" from the visitor identity on
 * the token, under a policy the database enforces. A client that named an id
 * would be asserting ownership, and an assertion is exactly what must not be
 * trusted here.
 */
export async function fetchThread(
  query: ThreadQuery = {},
  options: Cancellable = {},
): Promise<WidgetThread> {
  const params = new URLSearchParams();
  if (query.limit !== undefined) {
    params.set('limit', String(query.limit));
  }
  if (query.beforeSeq !== undefined) {
    params.set('beforeSeq', query.beforeSeq);
  }
  return apiRequest<WidgetThread>(withQuery(WIDGET_ROUTE.conversation.path, params), options);
}

export interface HistoryQuery {
  readonly limit?: number;
  /** Keyset cursor: the page of conversations last active BEFORE this instant. */
  readonly before?: string;
}

/**
 * The visitor's own conversations, most recent activity first (P2-52).
 *
 * KEYSET, not offset, and the contract says why: a history is read backwards
 * from now while the live conversation keeps moving to the top, so an offset
 * page two would skip a row or repeat one every time somebody replied. The
 * cursor is `nextBefore` from the previous page, which is null on the last.
 */
export async function fetchHistory(
  query: HistoryQuery = {},
  options: Cancellable = {},
): Promise<WidgetConversationList> {
  const params = new URLSearchParams();
  if (query.limit !== undefined) {
    params.set('limit', String(query.limit));
  }
  if (query.before !== undefined) {
    params.set('before', query.before);
  }

  return apiRequest<WidgetConversationList>(
    withQuery(WIDGET_ROUTE.conversations.path, params),
    options,
  );
}

/**
 * One of the visitor's PAST conversations, by id.
 *
 * ── WHY AN ID IN A PATH IS ALLOWED HERE, AND ONLY HERE ────────────────────
 *
 * Everything else on this surface refuses to name a conversation: `fetchThread`
 * takes no id, `sendMessage` takes no id, `closeConversation` takes no id, and
 * the contract's decision 1 is the reason — a client-supplied id must never be
 * an AUTHORISATION input, because "the server forgot to check it" is the failure
 * that design refuses.
 *
 * P2-52 / ADR-0142 amended that rule without weakening it. This id is a
 * SELECTOR among rows the visitor already owns, and the read runs under the
 * visitor context, so the RESTRICTIVE policy — `person_id = app_visitor_id()` on
 * every statement — is what decides whether the row exists for this caller. An
 * id belonging to another visitor, another brand or another tenant yields zero
 * rows and a 404 from the DATABASE rather than from a check a handler could
 * forget. The check is made impossible to omit instead of the id being made
 * impossible to send.
 *
 * `encodeURIComponent` on the way in because a path segment is being built, not
 * a string interpolated. The contract types the parameter as a UUID, so nothing
 * that reaches the server can need escaping — which is exactly why the escape
 * has to be here rather than trusted away: the day this id comes from somewhere
 * other than a response we parsed, the call site must already be safe.
 */
export async function fetchPastThread(
  conversationId: string,
  query: ThreadQuery = {},
  options: Cancellable = {},
): Promise<WidgetConversationThread> {
  const params = new URLSearchParams();
  if (query.limit !== undefined) {
    params.set('limit', String(query.limit));
  }
  if (query.beforeSeq !== undefined) {
    params.set('beforeSeq', query.beforeSeq);
  }

  const path = WIDGET_ROUTE.conversationById.path.replace(
    '{conversationId}',
    encodeURIComponent(conversationId),
  );

  return apiRequest<WidgetConversationThread>(withQuery(path, params), options);
}

/**
 * End the visitor's current conversation (P2-52, ADR-0142).
 *
 * NO BODY AND NO ID. Which conversation ends is resolved from the session, the
 * same way the live read resolves which one to serve, so this call cannot be
 * pointed at anything — including at the past conversation the visitor happens
 * to be reading.
 *
 * Idempotent by construction on the server: closing an already-closed
 * conversation returns it unchanged with a 200, because a visitor who taps "End
 * chat" twice on a slow connection did one thing. 404 means there was no
 * conversation to end.
 */
export async function closeConversation(options: Cancellable = {}): Promise<WidgetConversation> {
  return apiRequest<WidgetConversation>(WIDGET_ROUTE.closeConversation.path, {
    method: 'POST',
    ...options,
  });
}

/**
 * The site's player signed out (ADR-0249 §4): end this session and forget the
 * device. Carries the token (which session) AND the cookies (which continuity
 * credential), and the response deletes that cookie. The caller drops the token
 * whatever the answer — a sign-out that kept the token because the network
 * hiccupped would leave the player signed in on a shared screen.
 */
export async function endSession(options: Cancellable = {}): Promise<WidgetSessionEnded> {
  return apiRequest<WidgetSessionEnded>(WIDGET_ROUTE.endSession.path, {
    method: 'POST',
    cookies: true,
    ...options,
  });
}

export async function sendMessage(
  request: WidgetSendMessageRequest,
  options: Cancellable = {},
): Promise<WidgetSendMessageResult> {
  return apiRequest<WidgetSendMessageResult>(WIDGET_ROUTE.sendMessage.path, {
    method: 'POST',
    body: request,
    ...options,
  });
}

/**
 * Record what this browser reported, on the person this session acts as.
 *
 * REPLACES the whole snapshot, and that is the property the two consent-gated
 * fields depend on: a body without them CLEARS the stored values. So the same
 * call serves the first send (nothing granted), a grant (they appear), and a
 * WITHDRAWAL (they disappear again) — which is Art. 7(3)'s "as easy to withdraw
 * as to give" expressed as the identical request with two fewer keys.
 *
 * NO PERSON ID, for the same reason `fetchThread` takes no conversation id: the
 * server resolves who this is from the session token, and a client that named an
 * id would be asserting ownership of a row.
 *
 * `PUT` rather than `POST` because a retry is not a second write. A widget lives
 * on flaky mobile connections and this call is fired from an effect that can
 * re-run; the verb says the retry is safe, and the server's handler is what
 * makes it true.
 */
export async function putVisitorContext(
  request: WidgetVisitorContextRequest,
  options: Cancellable = {},
): Promise<WidgetVisitorContextResult> {
  return apiRequest<WidgetVisitorContextResult>(WIDGET_ROUTE.visitorContext.path, {
    method: 'PUT',
    body: request,
    ...options,
  });
}
